// src/components/shared/AnalyticsPanel.jsx

import React, { useState, useEffect } from 'react';
import { Eye, TrendingUp, Calendar, Activity } from 'lucide-react';
import { analyticsService } from '../../services/analyticsService';

export const AnalyticsPanel = ({ bookId, compact = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    loadAnalytics();
  }, [bookId, timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const days = timeRange === 'day' ? 1 : timeRange === 'week' ? 7 : 30;
      const data = await analyticsService.getBookAnalytics(bookId, days);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  if (!analytics) return null;

  const getTodayVisits = () => {
    const today = new Date().toISOString().split('T')[0];
    return analytics.dailyVisits?.[today] || 0;
  };

  const getWeekVisits = () => {
    const last7Days = Object.entries(analytics.dailyVisits || {})
      .slice(-7)
      .reduce((sum, [, count]) => sum + count, 0);
    return last7Days;
  };

  if (compact) {
    return (
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
              <Eye className="h-3 w-3" />
              <span className="text-xs font-medium">Today</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{getTodayVisits()}</p>
          </div>
          <div className="text-center border-x border-gray-200">
            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
              <Calendar className="h-3 w-3" />
              <span className="text-xs font-medium">Week</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{getWeekVisits()}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
              <TrendingUp className="h-3 w-3" />
              <span className="text-xs font-medium">Total</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{analytics.totalVisits}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-600" />
          Analytics
        </h4>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          <option value="day">Today</option>
          <option value="week">7 Days</option>
          <option value="month">30 Days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Eye className="h-4 w-4" />
            <span className="text-xs font-medium">Views</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{analytics.totalVisits}</p>
          <p className="text-xs text-gray-600 mt-1">{timeRange === 'day' ? 'today' : `last ${timeRange === 'week' ? '7' : '30'} days`}</p>
        </div>

        <div className="bg-purple-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs font-medium">Visitors</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{analytics.uniqueVisitors}</p>
          <p className="text-xs text-gray-600 mt-1">unique</p>
        </div>
      </div>
    </div>
  );
};